// تنبيهات نظام التشغيل (قرار المالك 01/10/2026): «الإشعارات عاوزينها تشتغل مع الويندوز وتدّي تنبيه يخلّيهم ياخدوا
// بالهم وهما شغالين… زي الواتساب بحيث يعرف إن فيه حاجة حصلت أو عميل جاله».
//
// على المتصفّح (ويندوز/ماك): Notification API — يُطلب الإذن مرّةً بضغطة من المستخدم (المتصفّح يرفض الطلب بلا ضغطة)،
// ثم يُعرض التنبيه حين تكون الصفحة مخفيّة أو بلا تركيز؛ وهي ظاهرة أمامه يكفي الصوت والبطاقة.
// داخل غلاف أندرويد لا توجد Notification API (تحتاج إضافةً أصلية وFCM — بندٌ مستقلّ)، فتعود الدوال بصمت.
const KEY = 'icontravel.systemNotify';

export const systemNotifySupported = (): boolean => typeof window !== 'undefined' && 'Notification' in window;

export type SystemNotifyState = 'unsupported' | 'default' | 'granted' | 'denied';
export const systemNotifyState = (): SystemNotifyState => {
  if (!systemNotifySupported()) return 'unsupported';
  return Notification.permission as SystemNotifyState;
};

/** يُطلب من ضغطة زرّ وحدها. يعيد الحالة بعد الطلب. */
export async function requestSystemNotify(): Promise<SystemNotifyState> {
  if (!systemNotifySupported()) return 'unsupported';
  try {
    const r = await Notification.requestPermission();
    try { localStorage.setItem(KEY, r === 'granted' ? '1' : '0'); } catch { /* تخزين ممنوع */ }
    return r as SystemNotifyState;
  } catch { return systemNotifyState(); }
}

/**
 * **إشعارات الجهاز** (0145): بعد الإذن يُسجَّل اشتراك Web Push لهذا المتصفّح عند الخادم، فتصل التنبيهات ولو أُغلق
 * تبويب البرنامج (ما دام المتصفّح يعمل). يُنادى بعد الإذن وعند كل فتحٍ للبرنامج (يجدّد ما انتهى). وفي غلاف أندرويد
 * لا Push API — فيرجع بصمت.
 */
export async function ensurePushSubscription(
  getKey: () => Promise<string>, save: (sub: PushSubscriptionJSON) => Promise<unknown>,
): Promise<boolean> {
  try {
    if (systemNotifyState() !== 'granted' || !('serviceWorker' in navigator) || !('PushManager' in window)) return false;
    const reg = await navigator.serviceWorker.ready;
    let sub = await reg.pushManager.getSubscription();
    if (!sub) {
      const key = await getKey();
      sub = await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: urlB64(key) });
    }
    await save(sub.toJSON());
    return true;
  } catch { return false; }
}
/**
 * **الخروج يوقف إشعارات هذا المتصفّح** (قرار المالك 03/10/2026: «لو سجّل خروج من البرنامج الإشعار تقف»): يُلغى الاشتراك
 * عند الخادم (والجلسة ما زالت صالحة) ثم في المتصفّح نفسه — فلا يصل لهذا الجهاز إشعارُ صاحبه بعد خروجه.
 */
export async function dropPushSubscription(unsave: (endpoint: string) => Promise<unknown>): Promise<void> {
  try {
    if (!('serviceWorker' in navigator)) return;
    const reg = await navigator.serviceWorker.getRegistration();
    const sub = await reg?.pushManager?.getSubscription();
    if (!sub) return;
    await unsave(sub.endpoint).catch(() => undefined);
    await sub.unsubscribe().catch(() => undefined);
  } catch { /* لا اشتراك */ }
}

function urlB64(base64: string): Uint8Array<ArrayBuffer> {
  const pad = '='.repeat((4 - (base64.length % 4)) % 4);
  const raw = atob((base64 + pad).replace(/-/g, '+').replace(/_/g, '/'));
  const out = new Uint8Array(new ArrayBuffer(raw.length));
  for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i);
  return out;
}

/** هل الصفحة أمام المستخدم الآن؟ حينها لا تنبيهَ نظام — الصوت والبطاقة يكفيان */
const pageInFront = (): boolean => {
  try { return document.visibilityState === 'visible' && document.hasFocus(); } catch { return true; }
};

/**
 * تنبيهٌ من النظام إن أُذن به والصفحةُ ليست أمام المستخدم. `tag` يمنع تكرار نفس التنبيه، والضغط عليه يُحضر الصفحة.
 */
export function systemNotify(title: string, body: string, tag?: string): boolean {
  if (systemNotifyState() !== 'granted' || pageInFront()) return false;
  try {
    const n = new Notification(title, { body: body.length > 200 ? body.slice(0, 200) + '…' : body, tag, icon: '/icons/icon-192.png', lang: 'ar', dir: 'rtl' });
    n.onclick = () => { try { window.focus(); n.close(); } catch { /* نافذة مغلقة */ } };
    return true;
  } catch { return false; }
}
